import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider as SupabaseAuthProvider, useAuth as useSupabaseAuth } from '@/lib/SupabaseAuthContext';
import { DataProvider } from '@/lib/DataContext';
import { useData } from '@/lib/DataContext';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from '@/components/ProtectedRoute';
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PersistentTabs = lazy(() => import('@/components/PersistentTabs'));
const TutorialOverlay = lazy(() => import('@/components/TutorialOverlay'));
import LoadingScreen from '@/components/LoadingScreen';
import OfflineBanner from '@/components/OfflineBanner';
import { loadCountryFeatures, regionsReadyPromise } from '@/lib/worldData';
import { routesReadyPromise } from '@/lib/navigation/pathHistory';
import { getCachedLocation, nextThemeBoundary, normalizeThemeMode, requestThemeLocation, resolveTheme, watchThemeLocation } from '@/lib/theme';
import { addMemoryPressureListener, getMemorySnapshot } from '@/lib/memoryGuard';
import { TutorialProvider } from '@/lib/TutorialContext';
import { PUBLIC_GUEST_MODE } from '@/lib/guestMode';
// Add page imports here

const AuthenticatedApp = () => {
  const { profile, profileReady, authChecked, isAuthenticated } = useSupabaseAuth();
  const { dataReady } = useData();
  // Map GeoJSON readiness — the loader stays until the country data is
  // loaded so authenticated users never see the empty map / UI pop-in.
  // Only waited on for authenticated users (login/register don't need it).
  const [mapReady, setMapReady] = useState(false);
  const [regionsReady, setRegionsReady] = useState(false);
  const [routesReady, setRoutesReady] = useState(false);
  // Error state: if critical data fails to load, show recovery UI
  const [dataError, setDataError] = useState(false);
  // Keep the shell covered until the profile, user data, map geometry, region
  // layer, and route overlay have all completed their first render pass.
  const [loaderVisible, setLoaderVisible] = useState(true);
  const [loaderFading, setLoaderFading] = useState(false);
  const [memoryPressure, setMemoryPressure] = useState(() => getMemorySnapshot());

  useEffect(() => {
    const stop = addMemoryPressureListener((snapshot) => {
      setMemoryPressure(snapshot);
    });
    return stop;
  }, []);

  useEffect(() => {
    if (PUBLIC_GUEST_MODE || !authChecked || !isAuthenticated) return;
    let active = true;
    setDataError(false);
    loadCountryFeatures()
      .then(() => { if (active) setMapReady(true); })
      .catch(() => { if (active) { setMapReady(true); setDataError(true); } });
    return () => { active = false; };
  }, [authChecked, isAuthenticated]);

  // Wait for the region layer and visible basemap tiles to render. A timeout
  // prevents a transient tile or region failure from trapping the app forever.
  useEffect(() => {
    if (!authChecked || !isAuthenticated || !mapReady) return;
    let active = true;
    const timeout = setTimeout(() => { if (active) setRegionsReady(true); }, 9000);
    regionsReadyPromise.then(() => {
      if (!active) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (active) setRegionsReady(true);
        });
      });
    });
    return () => { active = false; clearTimeout(timeout); };
  }, [authChecked, isAuthenticated, mapReady]);

  // Keep the loader visible until the tracked route overlay has completed its
  // first fetch/snap pass. Otherwise the route lines can appear after the map
  // is already considered ready and the loader fades out.
  useEffect(() => {
    if (!authChecked || !isAuthenticated || !mapReady) return;
    let active = true;
    const timeout = setTimeout(() => { if (active) setRoutesReady(true); }, 12000);
    routesReadyPromise.then(() => {
      if (!active) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (active) setRoutesReady(true);
        });
      });
    });
    return () => { active = false; clearTimeout(timeout); };
  }, [authChecked, isAuthenticated, mapReady]);

  const ready = PUBLIC_GUEST_MODE || (authChecked && (!isAuthenticated || (
    profileReady && dataReady && mapReady && regionsReady && routesReady
  )));
  useEffect(() => {
    if (ready && loaderVisible && !loaderFading) {
      setLoaderFading(true);
      const t = setTimeout(() => setLoaderVisible(false), 500);
      return () => clearTimeout(t);
    }
  }, [ready, loaderVisible, loaderFading]);

  // Apply the account theme preference. Auto uses the user's current location
  // and refreshes at the next sunrise/sunset boundary or app foreground.
  useEffect(() => {
    let active = true;
    let boundaryTimer = null;
    let currentLocation = null;
    const apply = (mode, location) => {
      const effective = resolveTheme(mode, location);
      document.documentElement.classList.toggle('dark', effective === 'dark');
    };
    const scheduleBoundary = (location) => {
      if (boundaryTimer) clearTimeout(boundaryTimer);
      boundaryTimer = setTimeout(refresh, nextThemeBoundary(location));
    };
    const refresh = () => {
      const mode = normalizeThemeMode(profile);
      if (mode !== 'auto') {
        apply(mode, null);
        return;
      }
      apply(mode, currentLocation);
      scheduleBoundary(currentLocation);
      requestThemeLocation().then((location) => {
        if (!active) return;
        currentLocation = location;
        apply(mode, location);
        scheduleBoundary(location);
      });
    };
    const stopLocationWatch = normalizeThemeMode(profile) === 'auto'
      ? watchThemeLocation((location) => {
        if (!active) return;
        currentLocation = location;
        apply('auto', location);
        scheduleBoundary(location);
      })
      : () => {};
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    currentLocation = getCachedLocation();
    if (profile || !isAuthenticated) refresh();
    return () => {
      active = false;
      if (boundaryTimer) clearTimeout(boundaryTimer);
      stopLocationWatch();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [profile, isAuthenticated]);

  // Render the main app. The loading overlay stays mounted on top until
  // auth + map data are ready, then fades out — so users never see the map
  // or UI elements popping in.
  const memorySafeMode = memoryPressure.critical || memoryPressure.low;

  return (
    <>
      <OfflineBanner />
      {loaderVisible && <LoadingScreen fadingOut={loaderFading} error={dataError} />}
      {memorySafeMode && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[2000] flex justify-center px-4 pt-3">
          <div className="rounded-full border border-amber-500/30 bg-amber-400/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-amber-200 backdrop-blur-sm">
            Low memory mode
          </div>
        </div>
      )}
      <div className="relative h-full w-full">
        <Suspense fallback={<div className="flex h-full w-full items-center justify-center bg-background text-xs text-foreground/50">Loading…</div>}>
          <Routes>
          <Route path="/login" element={PUBLIC_GUEST_MODE ? <Navigate to="/" replace /> : <Login />} />
          <Route path="/register" element={PUBLIC_GUEST_MODE ? <Navigate to="/" replace /> : <Register />} />
          <Route path="/forgot-password" element={PUBLIC_GUEST_MODE ? <Navigate to="/" replace /> : <ForgotPassword />} />
          <Route path="/reset-password" element={PUBLIC_GUEST_MODE ? <Navigate to="/" replace /> : <ResetPassword />} />
          <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
            <Route element={<PersistentTabs />}>
              <Route path="/" element={null} />
              <Route path="/statistics" element={null} />
              <Route path="/bucket-list" element={null} />
              <Route path="/travel-diary" element={null} />
              <Route path="/rankings" element={null} />
              <Route path="/admin" element={null} />
              <Route path="/settings" element={null} />
            </Route>
          </Route>
          <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </div>
      {isAuthenticated && !PUBLIC_GUEST_MODE && <TutorialOverlay />}
    </>
  );
};


function App() {

  return (
    <TutorialProvider>
      <SupabaseAuthProvider>
        <DataProvider>
          <QueryClientProvider client={queryClientInstance}>
          <Router>
            <ScrollToTop />
            <AuthenticatedApp />
          </Router>
          <Toaster />
          </QueryClientProvider>
        </DataProvider>
      </SupabaseAuthProvider>
    </TutorialProvider>
  )
}

export default App